<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

// Product Editor Tab Components
import ProductInformationTab from './product/InformationTab.vue';
import ProductMediaTab from './product/MediaTab.vue';
import ProductTestimonialTab from './product/TestimonialTab.vue';
import ProductCertificateTab from './product/CertificateTab.vue';
import ProductPriceTab from './product/PriceTab.vue';
import ProductLinksTab from './product/LinksTab.vue';
import ProductAnswerTemplateTab from './product/AnswerTemplateTab.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
  visibleTabs: {
    type: Array,
    default: null,
  },
  initialTab: {
    type: String,
    default: 'information',
  },
  titleOverride: {
    type: String,
    default: '',
  },
  descriptionOverride: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { t } = useI18n();
const isLoading = ref(false);

// Products from store
const products = computed(() => store.getters['aiCloser/getProducts'] || []);
const uiFlags = computed(() => store.getters['aiCloser/getUIFlags']);

// Selected product for editing
const selectedProduct = ref(null);
const activeProductTab = ref(props.initialTab || 'information');

// Add Product Dialog
const showAddDialog = ref(false);
const newProduct = reactive({
  name: '',
  series: '',
  description: '',
});

const isAIGamer = computed(
  () => props.assistant?.config?.wlab_sidebar_key === 'ai_gamer'
);

// Fetch products on mount
const fetchProducts = async () => {
  if (!props.assistant?.id) return;
  isLoading.value = true;
  try {
    await store.dispatch('aiCloser/fetchProducts', {
      assistantId: props.assistant.id,
    });
  } catch (error) {
    useAlert(
      error?.message || t('AILAB.ASSISTANT_V2.PRODUCT.ALERTS.LOAD_FAILED')
    );
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchProducts();
});

watch(
  () => props.assistant?.id,
  () => {
    fetchProducts();
  }
);

watch(
  () => [props.initialTab, productTabs.value.map(tab => tab.key).join(',')],
  () => {
    if (!productTabs.value.some(tab => tab.key === activeProductTab.value)) {
      activeProductTab.value = defaultTabKey.value;
    }
  },
  { immediate: true }
);

// Product editor tabs (AI gamer can override labels + hide irrelevant tabs)
const productTabs = computed(() => {
  const base = [
    {
      key: 'information',
      label: t('AILAB.ASSISTANT_V2.PRODUCT.TABS.INFORMATION'),
      icon: 'i-lucide-info',
    },
    {
      key: 'media',
      label: t('AILAB.ASSISTANT_V2.PRODUCT.TABS.MEDIA'),
      icon: 'i-lucide-image',
    },
    {
      key: 'testimonial',
      label: t('AILAB.ASSISTANT_V2.PRODUCT.TABS.TESTIMONIAL'),
      icon: 'i-lucide-message-circle',
    },
    {
      key: 'certificate',
      label: t('AILAB.ASSISTANT_V2.PRODUCT.TABS.CERTIFICATE'),
      icon: 'i-lucide-award',
    },
    {
      key: 'price',
      label: t('AILAB.ASSISTANT_V2.PRODUCT.TABS.PRICE'),
      icon: 'i-lucide-dollar-sign',
    },
    {
      key: 'answer-template',
      label: t('AILAB.ASSISTANT_V2.PRODUCT.TABS.ANSWER_TEMPLATE'),
      icon: 'i-lucide-file-question',
    },
  ];

  const tabs = !isAIGamer.value
    ? base
    : [
    {
      key: 'information',
      label: t('AILAB.ASSISTANT_V2.PRODUCT.TABS_AIGAMER.INFORMATION'),
      icon: 'i-lucide-info',
    },
    {
      key: 'media',
      label: t('AILAB.ASSISTANT_V2.PRODUCT.TABS_AIGAMER.MEDIA'),
      icon: 'i-lucide-image',
    },
    {
      key: 'links',
      label: t('AILAB.ASSISTANT_V2.PRODUCT.TABS_AIGAMER.LINKS'),
      icon: 'i-lucide-link-2',
    },
    {
      key: 'price',
      label: t('AILAB.ASSISTANT_V2.PRODUCT.TABS_AIGAMER.PROMOTIONS'),
      icon: 'i-lucide-dollar-sign',
    },
  ];

  if (!Array.isArray(props.visibleTabs) || !props.visibleTabs.length) return tabs;
  return tabs.filter(tab => props.visibleTabs.includes(tab.key));
});

const defaultTabKey = computed(
  () => productTabs.value[0]?.key || props.initialTab || 'information'
);

const resolvedTitle = computed(
  () => props.titleOverride || t('AILAB.ASSISTANT_V2.PRODUCT.HEADER.TITLE')
);

const resolvedDescription = computed(
  () =>
    props.descriptionOverride ||
    t('AILAB.ASSISTANT_V2.PRODUCT.HEADER.DESCRIPTION')
);

const openAddDialog = () => {
  newProduct.name = '';
  newProduct.series = '';
  newProduct.description = '';
  showAddDialog.value = true;
};

const handleAddProduct = async () => {
  if (!newProduct.name) {
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT.ALERTS.NAME_REQUIRED'));
    return;
  }

  try {
    await store.dispatch('aiCloser/createProduct', {
      assistantId: props.assistant.id,
      data: {
        name: newProduct.name,
        series: newProduct.series,
        description: newProduct.description,
      },
    });
    showAddDialog.value = false;
    await fetchProducts();
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT.ALERTS.ADD_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('AILAB.ASSISTANT_V2.PRODUCT.ALERTS.ADD_FAILED')
    );
  }
};

const selectProduct = product => {
  selectedProduct.value = product;
  activeProductTab.value = defaultTabKey.value;
};

const closeProductEditor = () => {
  selectedProduct.value = null;
};

const deleteProduct = async productId => {
  if (!confirm(t('AILAB.ASSISTANT_V2.PRODUCT.ALERTS.DELETE_CONFIRM'))) return;

  try {
    await store.dispatch('aiCloser/deleteProduct', {
      assistantId: props.assistant.id,
      productId,
    });
    if (selectedProduct.value?.id === productId) {
      selectedProduct.value = null;
    }
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT.ALERTS.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('AILAB.ASSISTANT_V2.PRODUCT.ALERTS.DELETE_FAILED')
    );
  }
};

const getProductName = product =>
  product?.name ||
  product?.product_name ||
  product?.title ||
  `#${product?.id || ''}`.trim();

const getProductSeries = product =>
  product?.series || product?.product_series || product?.product_line || '';

const getProductDescription = product =>
  product?.description || product?.product_description || product?.desc || '';

const getProductCreatedAt = product =>
  product?.createdAt ||
  product?.created_at ||
  product?.createdOn ||
  product?.created_on ||
  '';
</script>

<template>
  <div class="product-tab">
    <!-- Header -->
    <div class="tab-header">
      <div class="header-left">
        <div class="header-icon">
          <span class="i-lucide-package size-5" />
        </div>
        <div>
          <h1 class="header-title">
            {{ resolvedTitle }}
          </h1>
          <p class="header-description">
            {{ resolvedDescription }}
          </p>
        </div>
      </div>
      <button v-if="!selectedProduct" class="add-btn" @click="openAddDialog">
        <span class="i-lucide-plus size-4" />
        {{ t('AILAB.ASSISTANT_V2.PRODUCT.ACTIONS.ADD_PRODUCT') }}
      </button>
    </div>

    <!-- Product List (when no product selected) -->
    <div v-if="!selectedProduct" class="products-grid">
      <div
        v-for="product in products"
        :key="product.id"
        class="product-card group"
        @click="selectProduct(product)"
      >
        <div class="product-card-header">
          <div class="product-avatar">
            <span class="i-lucide-package size-5 product-avatar-icon" />
          </div>
          <div class="product-actions">
            <button
              class="action-btn action-btn-danger"
              @click.stop="deleteProduct(product.id)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
        <div class="product-card-body">
          <div v-if="getProductSeries(product)" class="product-badge">
            {{ getProductSeries(product) }}
          </div>
          <h3 class="product-name">{{ getProductName(product) }}</h3>
          <p class="product-desc">
            {{ getProductDescription(product) || '—' }}
          </p>
        </div>
        <div class="product-card-footer">
          <span class="product-date">{{
            t('AILAB.ASSISTANT_V2.PRODUCT.META.CREATED', {
              date: getProductCreatedAt(product),
            })
          }}</span>
          <span class="i-lucide-chevron-right size-4 product-chevron" />
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="products.length === 0" class="empty-state">
        <div class="empty-icon">
          <span class="i-lucide-package size-12" />
        </div>
        <h3 class="text-lg font-semibold text-slate-900 mb-2">
          {{ t('AILAB.ASSISTANT_V2.PRODUCT.EMPTY.TITLE') }}
        </h3>
        <p class="text-sm text-slate-600 mb-6">
          {{ t('AILAB.ASSISTANT_V2.PRODUCT.EMPTY.SUBTITLE') }}
        </p>
        <button class="add-btn" @click="openAddDialog">
          <span class="i-lucide-plus size-4" />
          {{ t('AILAB.ASSISTANT_V2.PRODUCT.ACTIONS.ADD_PRODUCT') }}
        </button>
      </div>
    </div>

    <!-- Product Editor (when product selected) -->
    <div v-else class="product-editor">
      <!-- Back Button & Product Header -->
      <div class="editor-header">
        <button class="back-btn" @click="closeProductEditor">
          <span class="i-lucide-arrow-left size-4" />
          {{ t('AILAB.ASSISTANT_V2.PRODUCT.ACTIONS.BACK_TO_PRODUCTS') }}
        </button>
        <div class="editor-title">
          <span class="product-badge">{{ selectedProduct.series }}</span>
          <h2 class="text-base font-semibold text-slate-800">
            {{ selectedProduct.name }}
          </h2>
        </div>
      </div>

      <!-- Horizontal Tabs -->
      <div class="editor-tabs">
        <button
          v-for="tab in productTabs"
          :key="tab.key"
          class="editor-tab"
          :class="{ active: activeProductTab === tab.key }"
          @click="activeProductTab = tab.key"
        >
          <span class="size-4" :class="[tab.icon]" />
          {{ tab.label }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="editor-content">
        <ProductInformationTab
          v-if="activeProductTab === 'information'"
          :product="selectedProduct"
          :assistant-id="assistant.id"
        />
        <ProductMediaTab
          v-if="activeProductTab === 'media'"
          :product="selectedProduct"
          :assistant-id="assistant.id"
        />
        <ProductAnswerTemplateTab
          v-if="!isAIGamer && activeProductTab === 'answer-template'"
          :product="selectedProduct"
          :assistant-id="assistant.id"
        />
        <ProductLinksTab
          v-if="isAIGamer && activeProductTab === 'links'"
          :product="selectedProduct"
          :assistant-id="assistant.id"
        />
        <ProductTestimonialTab
          v-if="!isAIGamer && activeProductTab === 'testimonial'"
          :product="selectedProduct"
          :assistant-id="assistant.id"
        />
        <ProductCertificateTab
          v-if="!isAIGamer && activeProductTab === 'certificate'"
          :product="selectedProduct"
          :assistant-id="assistant.id"
        />
        <ProductPriceTab
          v-if="activeProductTab === 'price'"
          :product="selectedProduct"
          :assistant-id="assistant.id"
        />
      </div>
    </div>

    <!-- Add Product Dialog -->
    <Teleport to="body">
      <div
        v-if="showAddDialog"
        class="dialog-overlay"
        @click.self="showAddDialog = false"
      >
        <div class="dialog-content">
          <div class="dialog-header">
            <h3 class="dialog-title">
              {{ t('AILAB.ASSISTANT_V2.PRODUCT.DIALOG.TITLE') }}
            </h3>
            <button class="dialog-close" @click="showAddDialog = false">
              <span class="i-lucide-x size-5" />
            </button>
          </div>
          <div class="dialog-body">
            <div class="form-group">
              <label class="form-label"
                >{{
                  t('AILAB.ASSISTANT_V2.PRODUCT.DIALOG.PRODUCT_NAME')
                }}
                *</label
              >
              <input
                v-model="newProduct.name"
                type="text"
                class="form-input"
                :placeholder="
                  t('AILAB.ASSISTANT_V2.PRODUCT.DIALOG.PLACEHOLDERS.NAME')
                "
              />
            </div>
            <div class="form-group">
              <label class="form-label">{{
                t('AILAB.ASSISTANT_V2.PRODUCT.DIALOG.SERIES')
              }}</label>
              <input
                v-model="newProduct.series"
                type="text"
                class="form-input"
                :placeholder="
                  t('AILAB.ASSISTANT_V2.PRODUCT.DIALOG.PLACEHOLDERS.SERIES')
                "
              />
            </div>
            <div class="form-group">
              <label class="form-label">{{
                t('AILAB.ASSISTANT_V2.PRODUCT.DIALOG.DESCRIPTION')
              }}</label>
              <textarea
                v-model="newProduct.description"
                class="form-textarea"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT.DIALOG.PLACEHOLDERS.DESCRIPTION'
                  )
                "
                rows="3"
              />
            </div>
          </div>
          <div class="dialog-footer">
            <button class="cancel-btn" @click="showAddDialog = false">
              {{ t('AILAB.FORM.CANCEL') }}
            </button>
            <button class="add-btn" @click="handleAddProduct">
              <span class="i-lucide-plus size-4" />
              {{ t('AILAB.ASSISTANT_V2.PRODUCT.ACTIONS.ADD_PRODUCT') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.product-tab {
  max-width: 1000px;
  position: relative;
}

.tab-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}

:global(body:not(.dark) .tab-header) {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #e5e7eb;
  margin: 0 0 4px 0;
}

:global(body:not(.dark) .header-title) {
  color: #1f2937;
}

.header-description {
  font-size: 14px;
  color: #a8aaae;
  margin: 0;
}

:global(body:not(.dark) .header-description) {
  color: #6b7280;
}

.header-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
}

:global(body:not(.dark) .header-icon) {
  background: linear-gradient(
    135deg,
    rgba(0, 102, 255, 0.15) 0%,
    rgba(0, 102, 255, 0.05) 100%
  );
  border: 1px solid rgba(0, 102, 255, 0.2);
  color: #0066ff;
}

:global(body.dark .header-icon) {
  background: #ffffff;
  color: #000000;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #e5e7eb;
  margin: 0;
}

:global(body:not(.dark) .header-title) {
  color: #1f2937;
}

.header-description {
  font-size: 14px;
  color: #a8aaae;
  margin: 4px 0 0 0;
}

:global(body:not(.dark) .header-description) {
  color: #6b7280;
}

.add-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 600;
  background: #ffffff;
  color: #000000;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

:global(body:not(.dark) .add-btn) {
  background: #0066ff;
  color: #ffffff;
}

:global(body.dark .add-btn) {
  background: #ffffff;
  color: #000000;
}

.add-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 102, 255, 0.4);
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.product-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.product-card:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.product-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.product-avatar {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-avatar-icon {
  color: #0f172a;
}

.product-chevron {
  color: #94a3b8;
  transition: all 0.2s;
}

.product-card:hover .product-chevron {
  color: #0f172a;
}

.product-actions {
  opacity: 0;
  transition: opacity 0.2s;
}

.product-card:hover .product-actions {
  opacity: 1;
}

.action-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--wlab-bg-input);
  border: none;
  border-radius: 8px;
  color: var(--wlab-text-tertiary);
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--wlab-text-primary);
}

.action-btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.product-card-body {
  margin-bottom: 16px;
}

.product-badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  color: #0066ff;
  background: rgba(0, 102, 255, 0.15);
  border-radius: 20px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.product-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--wlab-text-primary);
  margin-bottom: 4px;
}

.product-desc {
  font-size: 12px;
  color: var(--wlab-text-muted);
  line-height: 1.5;
}

.product-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid var(--wlab-border-light);
}

.product-date {
  font-size: 11px;
  color: var(--wlab-text-muted);
}

.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-icon {
  width: 100px;
  height: 100px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  margin-bottom: 24px;
}

:global(body:not(.dark) .empty-icon) {
  background: linear-gradient(
    135deg,
    rgba(0, 102, 255, 0.2) 0%,
    rgba(0, 102, 255, 0.05) 100%
  );
  border: 1px solid rgba(0, 102, 255, 0.2);
  color: #0066ff;
}

:global(body.dark .empty-icon) {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

/* Product Editor Styles */
.product-editor {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--wlab-border-light);
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--wlab-text-tertiary);
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
}

.back-btn:hover {
  color: #0066ff;
}

.editor-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.editor-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  background: var(--wlab-bg-tertiary);
  border-radius: 12px;
  overflow-x: auto;
}

.editor-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 12px;
  font-weight: 500;
  color: var(--wlab-text-muted);
  background: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.editor-tab:hover {
  color: var(--wlab-text-secondary);
  background: var(--wlab-bg-input);
}

.editor-tab.active {
  color: #000000;
  background: rgba(15, 23, 42, 0.04);
}

:global(body:not(.dark) .editor-tab.active) {
  color: #000000;
  background: rgba(15, 23, 42, 0.04);
}

:global(body.dark .editor-tab.active) {
  color: #000000;
  background: rgba(15, 23, 42, 0.04);
}

.editor-content {
  padding: 24px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 16px;
}

/* Dialog Styles */
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.dialog-content {
  width: 100%;
  max-width: 480px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.dialog-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}

.dialog-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: none;
  border-radius: 8px;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s;
}

.dialog-close:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.dialog-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
}

.cancel-btn {
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  background: #f8fafc;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 11px;
  font-weight: 600;
  color: #0f172a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  color: #0f172a;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #cbd5e1;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}

.form-input::placeholder,
.form-textarea::placeholder {
  color: var(--wlab-text-muted);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}
</style>
